import type {
  Action,
  ActionKind,
  Channel,
  FloorKind,
  Person,
  PolicyMode,
  PolicyPreset,
  Problem,
  ServiceView,
} from "@pagerless/schema";
import type { PlanInfo, Project } from "../api";
import type { Tone } from "../components/Lcd";
import { lcdDuration, lcdText, type ProblemPhase } from "./format";

/**
 * Lights mean the same on every screen: red = needs you now, amber = something to check or in progress,
 * green = all fine, off = nothing yet.
 *
 * What the pager shows on each screen: the single most important thing on that screen. The pager is
 * the page's headline, and its screen is the limit: exactly two lines of at most LCD_MAX characters.
 * Never cut: each line is picked from candidates, longest first, ending in a short fixed fallback.
 * One pure function per screen (screen data → lines + light), each unit-tested against the limit.
 */
export type PagerView = { lines: [string, string]; tone: Tone };

/** Characters per pager line. The pager is sized so this many fit, plus the blinking cursor. */
export const LCD_MAX = 22;

/** The first candidate that fits one line, in LCD text. The last candidate must always fit (tests check). */
export function pick(...candidates: string[]): string {
  const lines = candidates.map((c) => lcdText(c).replace(/\s+/g, " ").trim());
  return lines.find((l) => l.length > 0 && l.length <= LCD_MAX) ?? lines.at(-1) ?? "";
}

const plural = (n: number, one: string, many = `${one}S`) => `${n} ${n === 1 ? one : many}`;

const view = (a: string, b: string, tone: Tone): PagerView => ({ lines: [pick(a), pick(b)], tone });

/** A short duration for words: "9 SEC", "5 MIN", "2 HR 5 MIN". */
export function shortDuration(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 60) return `${s} SEC`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m} MIN`;
  return m % 60 === 0 ? `${Math.floor(m / 60)} HR` : `${Math.floor(m / 60)} HR ${m % 60} MIN`;
}

const FLOOR_LCD: Record<FloorKind, string> = {
  payments_failing: "PAYMENTS FAILING",
  app_silent: "APP WENT QUIET",
  crash_loop: "APP KEEPS CRASHING",
  http_5xx: "PAGES FAILING",
};

/** "Payments stopped working" with the payments floor → "PAYMENTS FAILING"; else the title if it fits. */
export function problemShort(p: Problem): string {
  return p.decision.floor ? FLOOR_LCD[p.decision.floor] : pick(p.title, "1 PROBLEM OPEN");
}

const ASK_FALLBACK: Record<ActionKind, string> = {
  undo_update: "UNDO THE UPDATE?",
  restart: "RESTART IT?",
  add_capacity: "ADD CAPACITY?",
  pause: "PAUSE THE APP?",
  open_pr: "OPEN THE FIX?",
  email_customers: "EMAIL CUSTOMERS?",
  data_change: "CHANGE SAVED DATA?",
  custom: "OK TO FIX IT?",
};

/** "Undo the 3:08 update" → "UNDO 3:08 UPDATE?"; a title too long for the LCD falls back to the kind. */
export function askLine(action: Pick<Action, "title" | "kind">): string {
  const short = lcdText(action.title)
    .replace(/\b(THE|A|AN|YOUR)\b/g, "")
    .replace(/\s+/g, " ")
    .trim();
  return pick(`${short}?`, ASK_FALLBACK[action.kind]);
}

// ------------------------------------------------------------------ Home

export function homePager(n: {
  needsYou: number;
  /** Being fixed or taken by a person: not all good until the fix is confirmed. */
  inProgress: number;
  helperToday: number;
  youToday: number;
}): PagerView {
  const second = n.youToday > 0 ? `YOU FIXED ${n.youToday} TODAY` : `HELPER FIXED ${n.helperToday} TODAY`;
  if (n.needsYou > 0) return view(`${n.needsYou} NEEDS YOU`, second, "red");
  if (n.inProgress > 0) return view(`${n.inProgress} BEING FIXED`, second, "amber");
  return view("ALL GOOD", second, "green");
}

/** The top-bar chip: one line, the same states as the pager. Never "all good" while a fix is running. */
export function chipStatus(problems: Problem[] | null): { tone: Tone; text: string } {
  // Not loaded yet: an unlit light and no words. Never "all good" before we know (E1).
  if (problems === null) return { tone: "off", text: "..." };
  const open = problems.filter((p) => p.state === "open").length;
  const fixing = problems.filter((p) => p.state === "fixing").length;
  const taken = problems.filter((p) => p.state === "acked").length;
  if (open > 0) return { tone: "red", text: `${open} NEEDS YOU` };
  if (fixing > 0) return { tone: "amber", text: "FIXING NOW" };
  if (taken > 0) return { tone: "amber", text: "YOU TOOK IT" };
  return { tone: "green", text: "ALL GOOD" };
}

// ------------------------------------------------------------------ Problems

const DAY_MS = 86_400_000;

export function problemsPager(problems: Problem[], nowMs: number): PagerView {
  const open = problems.filter((p) => p.state === "open").length;
  const busy = problems.filter((p) => p.state === "fixing" || p.state === "acked").length;
  const startOfDay = new Date(nowMs).setHours(0, 0, 0, 0);
  const fixed = problems.filter((p) => p.state === "resolved" && p.resolvedAt !== null);
  const today = fixed.filter((p) => (p.resolvedAt ?? 0) >= startOfDay).length;
  const week = fixed.filter((p) => (p.resolvedAt ?? 0) >= nowMs - 7 * DAY_MS).length;
  // Closed, not fixed: a hand close isn't a fix, and only the fix log can tell them apart (bug 1).
  const second = today > 0 ? `${today} CLOSED TODAY` : `${week} CLOSED THIS WEEK`;
  if (open > 0) return view(`${open} NEEDS YOU`, second, "red");
  if (busy > 0) return view(`${busy} BEING FIXED`, second, "amber");
  return view("NOTHING OPEN", second, "green");
}

// ------------------------------------------------------------------ Problem detail

/** Who closed a fixed problem, for the pager's second line. */
export type FixedBy = { by: "you"; answeredMs: number } | { by: "helper" } | { by: "hand" };

export function problemPager(n: {
  problem: Problem;
  phase: ProblemPhase;
  waiting: Action | null;
  running: Action | null;
  /** Fixed screen only. */
  fixedBy: FixedBy;
  /** A fix the helper ran failed (so a person was paged). */
  fixFailed?: boolean;
}): PagerView {
  const { problem, phase } = n;
  const what = problemShort(problem);
  switch (phase) {
    case "needs_you":
      return view(n.waiting ? askLine(n.waiting) : n.fixFailed ? "FIX FAILED, NEEDS YOU" : "NEEDS YOU", what, "red");
    case "on_it":
      return view("YOU TOOK IT", what, "amber");
    case "fixing":
      return view(n.running?.state === "verifying" ? "CHECKING THE FIX" : "FIXING NOW", what, "amber");
    case "watching":
      return view("HELPER WATCHING", what, "amber");
    case "fixed": {
      const fixedIn = (problem.resolvedAt ?? problem.lastSeenAt) - problem.openedAt;
      // Your effort is one tap; the number is how soon after the page you tapped.
      const second =
        n.fixedBy.by === "you"
          ? pick(
              `YOU TAPPED IN ${shortDuration(n.fixedBy.answeredMs)}`,
              `YOU TAPPED IN ${Math.round(n.fixedBy.answeredMs / 3_600_000)} HR`,
              "1 TAP FROM YOU",
            )
          : n.fixedBy.by === "helper"
            ? "HELPER DID IT ALONE"
            : "CLOSED BY HAND";
      return view(pick(`FIXED IN ${lcdDuration(fixedIn)}`, `FIXED IN ${shortDuration(fixedIn)}`), second, "green");
    }
  }
}

// ------------------------------------------------------------------ Apps

export const HOST_NAME: Record<string, string> = {
  vercel: "Vercel",
  railway: "Railway",
  render: "Render",
  cloudflare: "Cloudflare",
  netlify: "Netlify",
  supabase: "Supabase",
  fly: "Fly.io",
};

/**
 * An app with an open problem is failing, whatever its heartbeat says: the pager never says "up" while
 * payments fail. A single app's second line says where it runs, never repeats the first.
 */
export function appsPager(projects: Project[], open: Problem[]): PagerView {
  if (projects.length === 0) return view("NO APPS YET", "ADD ONE", "off");
  const failing = projects.filter((p) => p.state === "down" || open.some((o) => o.projectId === p.id));
  const first = failing[0];
  if (first) {
    const n = open.filter((o) => failing.some((f) => f.id === o.projectId)).length;
    return view(
      failing.length > 1 ? `${failing.length} APPS FAILING` : pick(`${first.name} FAILING`, "1 APP FAILING"),
      n > 0 ? `${plural(n, "PROBLEM")} OPEN` : "APP IS DOWN",
      "red",
    );
  }
  const up = projects.filter((p) => p.state === "up").length;
  const waiting = projects.filter((p) => p.state === "waiting");
  const paused = projects.filter((p) => p.state === "paused").length;
  const w = waiting[0];
  if (w && up === 0) return view(pick(`${w.name}: WAITING`, "APP: WAITING"), "NO SIGNAL YET", "amber");
  const only = projects.length === 1 ? projects[0] : undefined;
  if (only)
    return view(
      pick(`${only.name}: UP`, "YOUR APP IS UP"),
      only.host ? `ON ${HOST_NAME[only.host] ?? only.host}` : "NO OPEN PROBLEMS",
      "green",
    );
  // Paused apps are paused on purpose; everything else is up.
  const tally = [`${up} UP`, paused > 0 ? `${paused} PAUSED` : "", waiting.length > 0 ? `${waiting.length} NEW` : ""]
    .filter(Boolean)
    .join(", ");
  return view("ALL APPS UP", tally, "green");
}

// ------------------------------------------------------------------ Helper rules

const PRESET_LCD: Record<PolicyPreset, string> = { careful: "CAREFUL", balanced: "BALANCED", handsoff: "HANDS-OFF" };

export function rulesPager(modes: PolicyMode[], preset: PolicyPreset | null): PagerView {
  const count = (m: PolicyMode) => modes.filter((x) => x === m).length;
  return view(
    `HELPER: ${preset ? PRESET_LCD[preset] : "CUSTOM"}`,
    `${count("alone")} ALONE, ${count("ask")} ASK`,
    "green",
  );
}

export function setupHelperPager(preset: PolicyPreset): PagerView {
  const second = { careful: "ASKS BEFORE ANY FIX", balanced: "SMALL FIXES ALONE", handsoff: "FIXES, TELLS AFTER" }[
    preset
  ];
  return view(PRESET_LCD[preset], second, "green");
}

// ------------------------------------------------------------------ People

export function peoplePager(people: Person[]): PagerView {
  const [first, next] = [...people].sort((a, b) => a.order - b.order);
  if (!first) return view("NOBODY ON CALL", "ADD SOMEONE", "red");
  const onCall = (p: Person) => pick(`${p.name} ON CALL`, `${p.name.split(" ")[0]} ON CALL`, "1 PERSON ON CALL");
  return next
    ? view(onCall(first), pick(`${next.name} NEXT`, `${next.name.split(" ")[0]} NEXT`, "BACKUP IS SET"), "green")
    : view(onCall(first), "NO BACKUP YET", "amber");
}

// ------------------------------------------------------------------ Alerts

export const CHANNEL_NAME: Record<Channel["kind"], string> = {
  push_ios: "Phone",
  push_android: "Phone",
  web: "Browser",
  slack: "Slack",
  telegram: "Telegram",
  email: "Email",
  webhook: "Webhook",
  call: "Call",
};

export function alertsPager(channels: Channel[], nowMs: number): PagerView {
  const on = channels.filter((c) => c.levels.length > 0);
  if (on.length === 0) return view("NO ALERTS ON", "TURN ONE ON", "red");
  const failed = on.filter((c) => c.lastTestOk === false);
  const first = failed[0];
  if (first)
    return view(
      failed.length > 1 ? `${failed.length} CHANNELS FAILING` : `${CHANNEL_NAME[first.kind]} FAILING`,
      "FIX IT NOW",
      "red",
    );
  const tested = on.filter((c) => c.lastTestAt !== null);
  if (tested.length < on.length) return view(`${plural(on.length, "CHANNEL")} ON`, "NOT TESTED YET", "amber");
  const latest = Math.max(...tested.map((c) => c.lastTestAt ?? 0));
  const today = new Date(latest).toDateString() === new Date(nowMs).toDateString();
  const day = new Date(latest).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  return view("ALL CHANNELS OK", today ? "TESTED TODAY" : `TESTED ${day}`, "green");
}

// ------------------------------------------------------------------ Plan

/** Usage, not an alarm: a full free plan is fine (green). Lights mean the same everywhere. */
export function planPager(p: PlanInfo): PagerView {
  const people = p.people.limit === null ? `${p.people.used}` : `${p.people.used}/${p.people.limit}`;
  const apps = p.limits.apps === null ? `${p.apps}` : `${p.apps}/${p.limits.apps}`;
  return view(`${p.plan === "free" ? "FREE" : "TEAM"} PLAN`, `PEOPLE ${people}, APPS ${apps}`, "green");
}

// ------------------------------------------------------------------ Weekly

/** The weekly report: what the helper did and how often you were paged. Time to answer is secondary text. */
export function weeklyPager(w: { fixedByHelper: number; neededYou: number }): PagerView {
  return w.neededYou === 0
    ? view(`HELPER FIXED ${w.fixedByHelper}`, "NOBODY WAS PAGED", "green")
    : view(
        `HELPER FIXED ${w.fixedByHelper}`,
        `YOU ANSWERED ${w.neededYou} ${w.neededYou === 1 ? "PAGE" : "PAGES"}`,
        // A report, not a call to act: nothing here needs checking.
        "green",
      );
}

// ------------------------------------------------------------------ Setup: connect

/** Setup step 1. Once found, the app's display name ("MAYA'S SHOP"), never its service slug (C3). */
export function connectPager(n: { online: boolean; name: string | null }): PagerView {
  return n.online
    ? view(pick(n.name ?? "", "YOUR APP"), "IS CONNECTED", "green")
    : view("WAITING FOR", "YOUR APP", "amber");
}

/** /connect: a terminal waiting for the owner's Allow. The code is the headline: it must match the terminal. */
export function cliConnectPager(n: {
  code: string | null;
  state: "pending" | "approved" | "denied" | "used" | "expired" | "error";
}): PagerView {
  switch (n.state) {
    case "pending":
      return view(
        pick(`CODE ${n.code ?? ""}`, n.code ?? "", "CHECK THE CODE"),
        pick("ALLOW TERMINAL?", "ALLOW?"),
        "amber",
      );
    case "approved":
    case "used":
      return view("TERMINAL", "CONNECTED", "green");
    case "denied":
      return view("TERMINAL", "NOT ALLOWED", "off");
    case "expired":
      return view("CODE EXPIRED", "RUN IT AGAIN", "off");
    case "error":
      return view("CAN'T CHECK", "THE CODE", "red");
  }
}

/** Setup: the terminal connected things; the first signal is still to come. */
export function cliSetupPager(n: { services: number }): PagerView {
  return view(pick(`${plural(n.services, "SERVICE")} SET UP`, "SET UP"), "WAITING FOR DEPLOY", "amber");
}

/** While a screen's data loads. */
export const LOADING_PAGER: PagerView = { lines: ["LOADING", "ONE MOMENT"], tone: "off" };

// ------------------------------------------------------------------ Other tools (alert webhooks)

/** Before a key: say what to do. After: the count of tools ready, and the next step. */
export function sourcesPager(hasKey: boolean, tools: number): PagerView {
  if (!hasKey) return view("ALERTS FROM TOOLS", pick("MAKE A KEY TO START", "MAKE A KEY"), "off");
  return view(pick(`${tools} TOOLS READY`, "TOOLS READY"), pick("PASTE A URL IN ONE", "PASTE A URL"), "amber");
}

// ------------------------------------------------------------------ One app: its services

/**
 * The app's services: the first failing one by name ("API FAILING"), then how many are fine
 * ("14 OF 15 OK"). A service that went quiet is amber; no services yet is off.
 */
export function appPager(services: ServiceView[]): PagerView {
  const total = services.length;
  if (total === 0) return view("NO SERVICES YET", pick("RUN PAGERLESS CONNECT", "CONNECT TO START"), "off");
  const ok = services.filter((s) => s.status === "ok").length;
  const tally = pick(`${ok} OF ${total} OK`, `${ok} OK`);
  const failing = services.filter((s) => s.status === "failing");
  const first = failing[0];
  if (first)
    return view(
      failing.length > 1 ? `${failing.length} SERVICES FAILING` : pick(`${first.name} FAILING`, "1 SERVICE FAILING"),
      tally,
      "red",
    );
  const silent = services.filter((s) => s.status === "silent");
  const quiet = silent[0];
  if (quiet)
    return view(
      silent.length > 1 ? `${silent.length} WENT QUIET` : pick(`${quiet.name} WENT QUIET`, "1 WENT QUIET"),
      tally,
      "amber",
    );
  if (ok === 0) return view(plural(total, "SERVICE"), "NO SIGNAL YET", "off");
  return view(ok === total ? "ALL SERVICES OK" : `${plural(ok, "SERVICE")} OK`, tally, "green");
}

/** One service: its state by name, then the open problems or when it last sent anything. */
export function servicePager(s: ServiceView, nowMs: number): PagerView {
  const since = s.lastSignalAt === null ? "" : `LAST SIGNAL ${shortDuration(nowMs - s.lastSignalAt)} AGO`;
  switch (s.status) {
    case "failing":
      return view(
        pick(`${s.name} FAILING`, "SERVICE FAILING"),
        s.openProblems > 0 ? `${plural(s.openProblems, "PROBLEM")} OPEN` : "NEEDS A LOOK",
        "red",
      );
    case "silent":
      return view(pick(`${s.name} WENT QUIET`, "SERVICE WENT QUIET"), pick(since, "NO SIGNAL"), "amber");
    case "ok":
      return view(pick(`${s.name}: OK`, "SERVICE OK"), pick(since, "NO OPEN PROBLEMS"), "green");
    case "none":
      return view(pick(s.name, "THIS SERVICE"), "NO SIGNAL YET", "off");
  }
}
